import { useQuery } from "@tanstack/react-query";
import { ApiClient, HttpMethod, RequestOptions } from "../appfw/api-client/ApiClient";
import { encodeResourceUri } from "../eventer/utils/uri-encoding.ts";

export interface Invitation {
    readonly id: string;
    readonly createdAt: Date;
    readonly inviterId: string;
}

export interface Connections {
    readonly connections: string[];
}

export type ParticipationStatus = "accept" | "interested" | "reject";

export interface Idea {
    readonly id: string;

    // The event URI this idea is based on
    readonly eventUri?: string;
    readonly authorId?: string;
    readonly title: string;
    readonly description?: string;
    readonly startsAt?: string;
    readonly endsAt?: string;
    readonly accepted?: string[];
    readonly interested?: string[];
    readonly rejected?: string[];
}

export interface IdeaAccessors {
    readonly network: boolean;
    readonly userIds?: string[];
}

export interface IdeaShareResponse {
    readonly token: string;
}

export interface IdeaList {
    readonly ideas: Idea[];
    readonly cursor?: string;
}

export interface PodDto {
    readonly name: string;
    readonly enabled: boolean;
}

export interface PodsListDto {
    readonly pods: PodDto[];
}

class W2d2dApiClient extends ApiClient {

    constructor() {
        super(process.env.NODE_ENV === 'production' ? 'https://api.eventer.app/v1/w2d2d/' : 'http://localhost:8888/v1/w2d2d/')
    }

    appBaseUrl: string =
        process.env.NODE_ENV === 'production'
            ? (process.env.VITE_BETA === 'true' ? 'https://w2d2d-beta.eventer.app/' : 'https://w2d2d.eventer.app/')
            : 'http://localhost:5173/'

    async deleteAccount(opts?: RequestOptions): Promise<undefined> {
        return this.delete(`account`, opts)
    }

    async createInvitation(): Promise<Invitation> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Invitation>(
            `invitations`,
            {
                sessionId: sessionId,
            },
        )
    }

    async getInvitation(params: { invitationId: string }): Promise<Invitation> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Invitation>(
            `invitations/${params.invitationId}`,
            {
                method: HttpMethod.GET,
                sessionId: sessionId,
            },
        )
    }

    async acceptInvitation(params: { invitationId: string }): Promise<void> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<undefined>(
            `invitations/${params.invitationId}/accept`,
            {
                sessionId: sessionId,
            },
        )
    }

    async getConnections(): Promise<Connections> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Connections>(
            `users/me/connections`,
            {
                sessionId: sessionId,
                method: HttpMethod.GET,
            },
        )
    }

    async deleteConnection(params: { connectionId: string }) {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<undefined>(
            `users/me/connections/${params.connectionId}`,
            {
                sessionId: sessionId,
                method: HttpMethod.DELETE,
            },
        )
    }

    async newIdea(
        params: {
            title: string;
            description?: string;
            startsAt?: Date;
            endsAt?: Date;
            accessors?: IdeaAccessors;
        }
    ): Promise<Idea> {
        return this.post<Idea>(
            `ideas`,
            {
                title: params.title,
                description: params.description,
                startsAt: params.startsAt?.toISOString(),
                endsAt: params.endsAt?.toISOString(),
                accessors: params.accessors,
            },
        )
    }

    async newIdeaByEvent(
        params: {
            readonly eventUri: string;
            readonly accessors?: IdeaAccessors;
            readonly participationStatus?: ParticipationStatus;
        }
    ): Promise<Idea> {
        return this.post<Idea>(
            `ideas/by-event`,
            {
                eventUri: params.eventUri,
                accessors: params.accessors,
                participationStatus: params.participationStatus,
            },
        )
    }

    async getIdea(params: { id: string }): Promise<Idea> {
        return this.get(`ideas/${params.id}`)
    }

    async getIdeaByEvent(params: { uri: string }): Promise<Idea> {
        const encodedUri = encodeResourceUri(params.uri)
        return this.get(`ideas/by-event/${encodedUri}`)
    }

    async updateIdea(
        params: {
            id: string;
            title: string;
            description?: string;
            startsAt?: Date;
            endsAt?: Date;
            accessors?: IdeaAccessors;
        }
    ): Promise<Idea> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Idea>(
            `ideas/${params.id}`,
            {
                sessionId: sessionId,
                method: HttpMethod.PATCH,
                params: {
                    title: params.title,
                    description: params.description,
                    startsAt: params.startsAt?.toISOString(),
                    endsAt: params.endsAt?.toISOString(),
                    accessors: params.accessors,
                }
            },
        )
    }

    async getIdeas(): Promise<IdeaList> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<IdeaList>(
            `ideas`,
            {
                sessionId: sessionId,
                method: HttpMethod.GET,
            },
        )
    }

    async notifyIdea(params: { id: string, userIds?: string[] }) {
        return this.post<undefined>(
            `ideas/${params.id}/notify`,
            {
                userIds: params.userIds
            }
        )
    }

    async shareIdea(params: { id: string }): Promise<IdeaShareResponse> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<IdeaShareResponse>(
            `ideas/${params.id}/share`,
            {
                sessionId: sessionId,
            },
        )
    }

    async joinIdea(params: { id: string, shareToken: string }): Promise<undefined> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<undefined>(
            `ideas/${params.id}/join/${params.shareToken}`,
            {
                sessionId: sessionId,
            },
        )
    }

    async acceptIdea(params: { id: string }): Promise<Idea> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Idea>(
            `ideas/${params.id}/participation/accept`,
            {
                sessionId: sessionId,
                method: HttpMethod.POST,
            },
        )
    }

    async interestedInIdea(params: { id: string }): Promise<Idea> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Idea>(
            `ideas/${params.id}/participation/interested`,
            {
                sessionId: sessionId,
                method: HttpMethod.POST,
            },
        )
    }

    async rejectIdea(params: { id: string }): Promise<Idea> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Idea>(
            `ideas/${params.id}/participation/reject`,
            {
                sessionId: sessionId,
                method: HttpMethod.POST,
            },
        )
    }

    async getIdeaAccessors(params: { id: string }): Promise<IdeaAccessors> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<IdeaAccessors>(
            `ideas/${params.id}/accessors`,
            {
                sessionId: sessionId,
                method: HttpMethod.GET,
            },
        )
    }

    async deleteIdeaParticipation(params: { id: string }): Promise<Idea> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Idea>(
            `ideas/${params.id}/participation`,
            {
                sessionId: sessionId,
                method: HttpMethod.DELETE,
            },
        )
    }

    async deleteIdea(params: { id: string }): Promise<void> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<undefined>(
            `ideas/${params.id}`,
            {
                sessionId: sessionId,
                method: HttpMethod.DELETE,
            },
        )
    }

    async getPods(): Promise<PodsListDto> {
        return this.get(`pods`)
    }

    async addPod(params: { name: string }): Promise<void> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<undefined>(
            `pods/${params.name}`,
            {
                sessionId: sessionId,
                method: HttpMethod.PUT,
            },
        )
    }

    async enablePod(params: { name: string }): Promise<void> {
        return this.post<undefined>(
            `pods/${params.name}/enable`
        )
    }

    async disablePod(params: { name: string }): Promise<void> {
        return this.post<undefined>(
            `pods/${params.name}/disable`
        )
    }

    async deletePod(params: { name: string }): Promise<void> {
        return this.delete(`pods/${params.name}`)
    }
}

const w2d2dApiClient = new W2d2dApiClient();

export const useIdea = (id: string) => {
    return useQuery({
        queryKey: ['ideas', id],
        queryFn: () => w2d2dApiClient.getIdea({ id }),
        // staleTime: 1000 * 60 * 60, // 1h
    })
}

export { w2d2dApiClient };
